/*
 * Copyright © 2025 AionTrack. All rights reserved.
 */

import React, { createContext, useContext, useEffect, useState } from 'react'
import { supabase } from '@/lib/supabase'
import { usePostHog } from '@/contexts/PostHogContext'
import { AnalyticsEvents } from '@/lib/analytics/events'
import type { User, AuthContextType } from '@/types/auth'
import type { User as SupabaseUser } from '@supabase/supabase-js'

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

function transformSupabaseUser(supabaseUser: SupabaseUser | null): User | null {
  if (!supabaseUser) return null

  return {
    id: supabaseUser.id,
    email: supabaseUser.email || '',
    name: supabaseUser.user_metadata?.name,
    created_at: supabaseUser.created_at,
    updated_at: supabaseUser.updated_at,
  }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)

  // PostHogProvider always wraps AuthProvider (see main.tsx), so this is safe
  // to call unconditionally - calling hooks inside try/catch violates the
  // Rules of Hooks since call order isn't guaranteed if it throws partway.
  const posthog = usePostHog()

  useEffect(() => {
    // Check active sessions and sets the user
    supabase.auth.getSession().then(({ data: { session } }) => {
      const transformedUser = transformSupabaseUser(session?.user || null)
      setUser(transformedUser)
      setLoading(false)
      // Note: Don't identify here - onAuthStateChange will handle it
    })

    // Listen for changes on auth state (sign in, sign out, etc.)
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      const transformedUser = transformSupabaseUser(session?.user || null)
      setUser(transformedUser)
      setLoading(false)

      // Handle PostHog identification (only place we identify)
      if (posthog?.isReady && transformedUser) {
        posthog.identify(transformedUser.id, {
          email: transformedUser.email,
          name: transformedUser.name,
          created_at: transformedUser.created_at,
        })
      }
    })

    return () => subscription.unsubscribe()
  }, [posthog])

  const signUp = async (email: string, password: string) => {
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: `${window.location.origin}/projects`,
      },
    })
    if (error) throw error
  }

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    })
    if (error) throw error
  }

  const signOut = async () => {
    // Track logout before resetting
    if (posthog?.isReady) {
      posthog.track(AnalyticsEvents.AUTH_LOGOUT, {})
      posthog.reset()
    }

    const { error } = await supabase.auth.signOut()
    if (error) throw error
  }

  const resetPassword = async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    })
    if (error) throw error
  }

  const enrollMFA = async () => {
    const { data, error } = await supabase.auth.mfa.enroll({
      factorType: 'totp',
    })
    if (error) throw error
    if (!data) throw new Error('Failed to enroll MFA')

    return {
      qr_code: data.totp.qr_code,
      secret: data.totp.secret,
      factorId: data.id,
    }
  }

  const verifyMFAEnrollment = async (factorId: string, code: string) => {
    const challenge = await supabase.auth.mfa.challenge({ factorId })
    if (challenge.error) throw challenge.error

    const { error } = await supabase.auth.mfa.verify({
      factorId,
      challengeId: challenge.data.id,
      code,
    })
    if (error) throw error
  }

  const unenrollMFA = async () => {
    const factors = await supabase.auth.mfa.listFactors()
    if (factors.error) throw factors.error

    const totpFactor = factors.data?.totp?.[0]
    if (!totpFactor) throw new Error('No TOTP factor found')

    const { error } = await supabase.auth.mfa.unenroll({
      factorId: totpFactor.id,
    })
    if (error) throw error
  }

  const verifyMFAChallenge = async (code: string) => {
    const factors = await supabase.auth.mfa.listFactors()
    if (factors.error) throw factors.error

    const totpFactor = factors.data?.totp?.[0]
    if (!totpFactor) throw new Error('No TOTP factor found')

    const challenge = await supabase.auth.mfa.challenge({
      factorId: totpFactor.id,
    })
    if (challenge.error) throw challenge.error

    const { error } = await supabase.auth.mfa.verify({
      factorId: totpFactor.id,
      challengeId: challenge.data.id,
      code,
    })
    if (error) throw error
  }

  const checkMFAStatus = async () => {
    const { data, error } = await supabase.auth.mfa.listFactors()
    if (error) throw error

    return (data?.totp?.length ?? 0) > 0
  }

  const value: AuthContextType = {
    user,
    loading,
    signUp,
    signIn,
    signOut,
    resetPassword,
    enrollMFA,
    verifyMFAEnrollment,
    unenrollMFA,
    verifyMFAChallenge,
    checkMFAStatus,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
